import { useEffect, useRef, useState, useCallback } from 'react';
import { findConversions } from './imperialOverlay';

// In-app PDF reader (pdf.js → canvas) — replaces browser iframes so the BC Code
// works identically everywhere, including phones that download PDFs instead of
// embedding them. Virtualized: all pages get placeholder slots, but only pages
// near the viewport are actually rendered. Faithful rendering — tables in the
// code look exactly like the printed page.

interface PdfReaderProps {
  url: string;
  /** 1-based page to scroll to; changes are honored any time. */
  targetPage: number;
  /** Bump to force a re-jump even when targetPage is unchanged. */
  jumpNonce?: number;
  /** Terms to paint as highlights on the rendered pages (case-insensitive). */
  highlightTerms?: string[];
  onVisiblePage?: (page: number) => void;
}

interface TextItem { str: string; transform: number[]; width: number; height: number }
interface PdfViewport { width: number; height: number; scale: number; transform: number[] }
interface PdfDocLike {
  numPages: number;
  getPage: (n: number) => Promise<{
    getViewport: (opts: { scale: number }) => PdfViewport;
    render: (opts: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport }) => { promise: Promise<void> };
    getTextContent: () => Promise<{ items: TextItem[] }>;
  }>;
}

// 2D affine multiply (pdf.js Util.transform without importing it here)
const mul = (m: number[], t: number[]) => [
  m[0] * t[0] + m[2] * t[1],
  m[1] * t[0] + m[3] * t[1],
  m[0] * t[2] + m[2] * t[3],
  m[1] * t[2] + m[3] * t[3],
  m[0] * t[4] + m[2] * t[5] + m[4],
  m[1] * t[4] + m[3] * t[5] + m[5],
];

const RENDER_MARGIN = 2; // pages either side of the viewport to keep rendered

export function PdfReader({ url, targetPage, jumpNonce, highlightTerms, onVisiblePage }: PdfReaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<PdfDocLike | null>(null);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pageAspect, setPageAspect] = useState(11 / 8.5); // h/w, refined after load
  const [baseWidth, setBaseWidth] = useState(800);
  const [visibleRange, setVisibleRange] = useState<[number, number]>([1, 4]);
  // Imperial overlay — metric values get ≈ft-in chips painted beside them (default on;
  // BC framers read inches, the code prints millimetres)
  const [imperial, setImperial] = useState(() => localStorage.getItem('refImperial') !== '0');
  const renderedRef = useRef(new Map<number, HTMLCanvasElement>());
  const rafRef = useRef(0);

  // Load the document (pdf.js pulled in lazily)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        const d = await pdfjs.getDocument({ url }).promise;
        if (!alive) return;
        const p1 = await d.getPage(1);
        const vp = p1.getViewport({ scale: 1 });
        setPageAspect(vp.height / vp.width);
        setDoc(d as unknown as PdfDocLike);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load PDF');
      }
    })();
    return () => { alive = false; };
  }, [url]);

  // Fit-to-width base size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setBaseWidth(Math.max(280, el.clientWidth - 16));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pageW = baseWidth * zoom;
  const pageH = pageW * pageAspect;

  // Track which pages are near the viewport
  const updateVisible = useCallback(() => {
    const el = containerRef.current;
    if (!el || !doc) return;
    const slotH = pageH + 12;
    const first = Math.max(1, Math.floor(el.scrollTop / slotH) + 1 - RENDER_MARGIN);
    const last = Math.min(doc.numPages, Math.ceil((el.scrollTop + el.clientHeight) / slotH) + RENDER_MARGIN);
    setVisibleRange(([a, b]) => (a === first && b === last ? [a, b] : [first, last]));
    const current = Math.max(1, Math.min(doc.numPages, Math.round(el.scrollTop / slotH) + 1));
    onVisiblePage?.(current);
  }, [doc, pageH, onVisiblePage]);

  const onScroll = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(updateVisible);
  };

  const termsKey = (highlightTerms || []).map(t => t.toLowerCase()).filter(t => t.length >= 2).sort().join('|');

  // Render pages in the visible window
  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    const [first, last] = visibleRange;
    const terms = termsKey ? termsKey.split('|') : [];
    (async () => {
      for (let n = first; n <= last && !cancelled; n++) {
        const holder = document.getElementById(`pdfpg-${n}`);
        if (!holder) continue;
        const wanted = `${Math.round(pageW)}#${termsKey}#${imperial ? 'i' : ''}`;
        if (holder.dataset.rendered === wanted) continue;
        try {
          const page = await doc.getPage(n);
          const vp1 = page.getViewport({ scale: 1 });
          // Render at TRUE device resolution or text goes blurry on 2–3× phone
          // screens (a fixed 1.5× under-sampled). Cap backing width ~2600px so a
          // deep zoom on desktop can't allocate runaway canvases.
          const dpr = Math.min(window.devicePixelRatio || 1, 3, 2600 / pageW);
          const scale = (pageW / vp1.width) * Math.max(dpr, 1);
          const vp = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width;
          canvas.height = vp.height;
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
          if (cancelled) continue;
          // Paint keyword highlights straight onto the page so the eye lands on
          // the spec instantly — position from the PDF's own text geometry.
          if (terms.length || imperial) {
            try {
              const tc = await page.getTextContent();
              if (terms.length) {
                ctx.save();
                ctx.globalCompositeOperation = 'multiply';
                ctx.fillStyle = 'rgba(255, 214, 90, 0.65)';
                for (const item of tc.items) {
                  if (!item.str) continue;
                  const lower = item.str.toLowerCase();
                  for (const term of terms) {
                    let idx = lower.indexOf(term);
                    while (idx !== -1) {
                      const r = mul(vp.transform, item.transform);
                      const fontH = Math.hypot(r[2], r[3]) || 10;
                      const w = item.width * vp.scale;
                      const x0 = r[4] + (idx / item.str.length) * w;
                      const hw = Math.max((term.length / item.str.length) * w, 4);
                      ctx.fillRect(x0, r[5] - fontH, hw, fontH * 1.2);
                      idx = lower.indexOf(term, idx + term.length);
                    }
                  }
                }
                ctx.restore();
              }
              // Imperial chips: small green ≈-labels nestled into the interline
              // gap above each metric value, so the metric number stays legible.
              if (imperial) {
                ctx.save();
                ctx.textBaseline = 'alphabetic';
                for (const item of tc.items) {
                  if (!item.str || !/\d/.test(item.str)) continue;
                  const convs = findConversions(item.str);
                  if (!convs.length) continue;
                  const r = mul(vp.transform, item.transform);
                  const fontH = Math.hypot(r[2], r[3]) || 10;
                  const w = item.width * vp.scale;
                  for (const c of convs) {
                    const chipFont = Math.max(8, Math.min(14, fontH * 0.78));
                    ctx.font = `600 ${chipFont}px Manrope, ui-sans-serif, sans-serif`;
                    const tw = ctx.measureText(c.label).width;
                    const chipH = chipFont * 1.3;
                    const x = Math.max(2, Math.min(r[4] + (c.start / item.str.length) * w, vp.width - tw - 8));
                    let y = r[5] - fontH - chipH + fontH * 0.3;
                    if (y < 1) y = r[5] + 2;
                    ctx.fillStyle = 'rgba(22, 101, 52, 0.88)';
                    if (typeof ctx.roundRect === 'function') {
                      ctx.beginPath();
                      ctx.roundRect(x - 3, y, tw + 6, chipH, 3);
                      ctx.fill();
                    } else {
                      ctx.fillRect(x - 3, y, tw + 6, chipH);
                    }
                    ctx.fillStyle = '#fff';
                    ctx.fillText(c.label, x, y + chipH - chipFont * 0.32);
                  }
                }
                ctx.restore();
              }
            } catch { /* overlay failure is cosmetic */ }
          }
          holder.replaceChildren(canvas);
          holder.dataset.rendered = wanted;
          renderedRef.current.set(n, canvas);
        } catch { /* page render failed — placeholder remains */ }
      }
      // Drop far-away canvases to cap memory on the 1,906-page doc
      for (const [n, cv] of renderedRef.current) {
        if (n < first - 6 || n > last + 6) {
          cv.parentElement?.replaceChildren();
          const holder = document.getElementById(`pdfpg-${n}`);
          if (holder) delete holder.dataset.rendered;
          renderedRef.current.delete(n);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [doc, visibleRange, pageW, termsKey, imperial]);

  // Jump to target page
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !doc) return;
    const slotH = pageH + 12;
    el.scrollTop = (Math.max(1, Math.min(targetPage, doc.numPages)) - 1) * slotH;
    updateVisible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPage, jumpNonce, doc]);

  // Re-measure after zoom changes
  useEffect(() => { updateVisible(); }, [zoom, updateVisible]);

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 text-gray-500 text-sm p-6">
        <span className="material-symbols-outlined text-3xl text-gray-300">picture_as_pdf</span>
        <p>Couldn't open the PDF ({error}).</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#C77D3F] font-semibold">Open it directly instead</a>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {!doc && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm z-10">
          <span className="material-symbols-outlined animate-spin mr-2">progress_activity</span>
          Opening the code…
        </div>
      )}
      {/* Zoom controls */}
      <div className="absolute top-2 right-4 z-10 flex rounded-lg overflow-hidden border border-gray-300 shadow-sm">
        <button onClick={() => setImperial(v => { localStorage.setItem('refImperial', v ? '0' : '1'); return !v; })}
          className={`h-9 px-2.5 text-xs font-bold border-r border-gray-300 ${imperial ? 'bg-green-700 text-white' : 'bg-white/95 hover:bg-white text-gray-500'}`}
          title={imperial ? 'Imperial overlay on — metric values get ≈ft-in chips. Tap to hide.' : 'Show imperial (ft-in) beside metric values'}>
          ft-in
        </button>
        <button onClick={() => setZoom(z => Math.min(3, Math.round((z + 0.25) * 4) / 4))}
          className="h-9 w-10 bg-white/95 hover:bg-white text-gray-700 flex items-center justify-center" title="Zoom in">
          <span className="material-symbols-outlined text-lg">add</span>
        </button>
        <button onClick={() => setZoom(1)}
          className="h-9 px-2 bg-white/95 hover:bg-white text-gray-600 text-xs font-semibold border-x border-gray-300" title="Fit width">
          {Math.round(zoom * 100)}%
        </button>
        <button onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.25) * 4) / 4))}
          className="h-9 w-10 bg-white/95 hover:bg-white text-gray-700 flex items-center justify-center" title="Zoom out">
          <span className="material-symbols-outlined text-lg">remove</span>
        </button>
      </div>

      <div ref={containerRef} onScroll={onScroll} className="h-full overflow-auto bg-gray-200 px-2 py-1.5">
        {doc && Array.from({ length: doc.numPages }, (_, i) => (
          <div
            key={i + 1}
            id={`pdfpg-${i + 1}`}
            className="mx-auto mb-3 bg-white shadow-sm flex items-center justify-center text-gray-300 text-xs"
            style={{ width: pageW, height: pageH }}
          >
            {i + 1}
          </div>
        ))}
      </div>
    </div>
  );
}
